import React from 'react';
import { ErrorPage, useFeatureFlags, useIsBlockedHelper } from '@cko/dashboard-shared';
import styled from 'styled-components';

import { LayoutGrid } from '@cko/primitives';
import { NoAccessPage } from '../../components/NoAccessPage';
import { useUserContextValidation } from '../../providers/UserContextValidationProvider';
import { useNavigation } from '../../providers/NavigationProvider';
import { FlattenedRoute, RoutePage } from '../../config/shared/types';
import { LayoutRenderer } from '../LayoutRenderer';
import { LayoutInstance, PageDefinition } from '../../config/shared/types/page';
import { FeatureFlagId } from '../../config/shared/enums/featureFlagIds';
import { BlockedPage } from '../../components/BlockedPage';
import { CONTAINERS } from './containers';
import { PageHeader } from './PageHeader';

const CHATBOT_MARGIN_PIXELS = 88;

type Props = {
  route: FlattenedRoute;
  routeId: string;
};

export const PageRenderer = ({ route, routeId }: Props) => {
  const { getPageById } = useNavigation();
  const { isBlocked } = useIsBlockedHelper(route.required_account_levels);
  const { validate } = useUserContextValidation();
  const { getFeatureFlag } = useFeatureFlags();
  const isChatbotEnabled = getFeatureFlag(FeatureFlagId.CHATBOT);

  if (!route.page) {
    // eslint-disable-next-line no-console
    console.log('Route doesnt have a page');
    return <ErrorPage />;
  }

  let pageDefinition: PageDefinition | undefined;

  if (Array.isArray(route.page)) {
    const filteredPage: RoutePage | undefined = route.page.filter((page) =>
      validate({ feature_flags: page.feature_flag }, 'PAGE'),
    )[0];
    if (filteredPage) {
      pageDefinition = getPageById(filteredPage.id);
    }
  } else if (route.page) {
    const validPage = validate({ feature_flags: route.page.feature_flag }, 'PAGE');
    if (validPage) {
      pageDefinition = getPageById(route.page.id);
    }
  }

  const ContainerComponent = CONTAINERS[pageDefinition?.type || 'default'];
  const shouldAddMargin = isChatbotEnabled && pageDefinition?.type !== 'full-screen-dialog';

  const PageContainer = shouldAddMargin
    ? styled(ContainerComponent)`
        margin-bottom: ${CHATBOT_MARGIN_PIXELS}px;
      `
    : ContainerComponent;

  if (!pageDefinition) {
    // eslint-disable-next-line no-console
    console.error('Invalid page definition');
    return <ErrorPage />;
  }

  const layoutInstance = pageDefinition?.layout_implementation;

  if (!layoutInstance) {
    // eslint-disable-next-line no-console
    console.log('No layout instance');
    return <ErrorPage />;
  }

  const header = pageDefinition.header ? (
    <PageHeader headerConfig={pageDefinition.header} route={route} routeId={routeId} />
  ) : null;

  if (isBlocked && route.required_account_levels) {
    return (
      <PageContainer pageDefinition={pageDefinition}>
        {header}
        <BlockedPage requiredAccountLevels={route.required_account_levels} />
      </PageContainer>
    );
  }

  // This currently does not handle nested content areas.
  const validContentAreas = Object.keys(layoutInstance.content_areas).reduce<
    LayoutInstance['content_areas']
  >((accumulatedValidContentAreas, key) => {
    accumulatedValidContentAreas[key] = layoutInstance.content_areas[key].filter(
      (contentInstance) => {
        if (contentInstance.type === 'view') {
          return validate(
            {
              feature_flags: contentInstance.feature_flags,
              permissions: contentInstance.permissions,
            },
            'VIEW',
          );
        }

        return true;
      },
    );

    return accumulatedValidContentAreas;
  }, {});

  const hasNoValidContentAreas = Object.keys(validContentAreas).every(
    (key) => validContentAreas[key].length === 0,
  );
  if (hasNoValidContentAreas) {
    return (
      <PageContainer pageDefinition={pageDefinition}>
        {header}
        <NoAccessPage />
      </PageContainer>
    );
  }

  const layoutInstanceWithValidContentAreas = {
    ...layoutInstance,
    content_areas: validContentAreas,
  };

  return (
    <PageContainer pageDefinition={pageDefinition}>
      {header}
      <LayoutGrid fullWidth={pageDefinition.is_full_width}>
        <LayoutRenderer layoutInstance={layoutInstanceWithValidContentAreas} />
      </LayoutGrid>
    </PageContainer>
  );
};
